import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '@/components/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { formatDateOnly, orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import { PersonAvatar } from '@/components/core/person-avatar/person-avatar';
import type { Presence } from '@/components/core/presence-avatar/presence-avatar';

const LEAVE_TYPE_KEYS: Record<string, string> = { FullDay: 'FULL_DAY', HalfDay: 'HALF_DAY' };

interface LeaveDetailsDialogProps {
  /** The opened leave and whose it is; the dialog is closed while this is undefined. */
  entry?: { leave: LeaveCalendarLeaveEvent; employee: LeaveCalendarEmployeeGroup };
  email?: string;
  /** Today's presence per person, for the avatar (as People shows it). */
  presenceOf?: (userId: string) => { presence?: Presence; presenceLabel?: string };
  onClose: () => void;
}

/** Prototype `.dl > div`: the label muted on the left, the value medium on the right, a hairline between. */
const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-center justify-between gap-4 border-b py-2 last:border-b-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="m-0 min-w-0 break-words text-right font-medium [overflow-wrap:anywhere]">
      {children}
    </dd>
  </div>
);

/**
 * One leave of the calendar (HR Module Prototype `calLeave`): who, the category, full or half day, the
 * dates, when it was requested and its status. The calendar's query carries no reason or notes, so
 * they are not shown.
 */
export const LeaveDetailsDialog = ({
  entry,
  email,
  presenceOf,
  onClose,
}: LeaveDetailsDialogProps) => {
  const { t } = useTranslation();
  const leave = entry?.leave;
  const employee = entry?.employee;
  const typeKey = leave?.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        {leave && employee && (
          <>
            <DialogHeader>
              <DialogTitle>{t('LEAVE_CALENDAR_LEAVE_DETAILS')}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center gap-3">
              <PersonAvatar
                name={employee.DisplayName}
                {...presenceOf?.(employee.ItemId)}
                size={40}
              />
              <div className="min-w-0">
                <b className="block truncate font-medium">{employee.DisplayName}</b>
                {email && <div className="truncate text-xs text-muted-foreground">{email}</div>}
              </div>
            </div>
            <dl className="m-0 grid text-sm">
              <Row label={t('LEAVE_TYPE')}>{leave.LeaveCategory || leave.Title}</Row>
              {typeKey && <Row label={t('LEAVE_CALENDAR_FULL_OR_HALF_DAY')}>{t(typeKey)}</Row>}
              <Row label={t('LEAVE_CALENDAR_DATES')}>
                {formatDateOnly(leave.StartDate) === formatDateOnly(leave.EndDate)
                  ? formatDateOnly(leave.StartDate)
                  : `${formatDateOnly(leave.StartDate)} – ${formatDateOnly(leave.EndDate)}`}
              </Row>
              {leave.CreateDate && (
                <Row label={t('LEAVE_CALENDAR_REQUESTED')}>
                  {formatDateOnly(orgCalendarDate(leave.CreateDate, leave.Timezone || undefined))}
                </Row>
              )}
              <Row label={t('STATUS')}>
                <StatusChip status={leave.Status}>{t(leave.Status.toUpperCase())}</StatusChip>
              </Row>
            </dl>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
